// src/components/FinancialIntelligence/FIThreadPanel.js
//
// Left panel showing the Financial Intelligence thread history for the selected client.
// Real-time Firestore listener on clients/{clientId}/financialIntelligence subcollection.
// Selects most recent entry by default on first load.

import React, { useState, useEffect, useRef, useMemo } from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  Button,
} from '@mui/material';
import {
  AccountBalance as FIIcon,
} from '@mui/icons-material';
import { collection, query, orderBy, onSnapshot, doc, updateDoc } from 'firebase/firestore';
import { db } from '../../firebase';
import FIEntryCard from './FIEntryCard';

const HISTORY_LABELS = {
  prepare_for_meeting: 'Pre-meeting brief',
  board_package: 'Board Package',
  understand_finances: 'Financial Health Diagnostic',
  decision_support: 'Decision Support',
  cash_flow_brief: 'Cash Flow Brief',
  cash_flow_deep_dive: 'Cash Flow Deep Dive',
  freeform: 'Analysis',
};

const FIThreadPanel = ({
  clientId,
  user,
  activeEntryId,
  streamingEntryId,
  onEntrySelect,
  onStreamingEntryPersisted,
  searchQuery,
  sortOption,
  dateFrom,
  dateTo,
  onFilteredCountChange,
  onClearFilters,
  showAllHistory,
  onShowAllHistory,
  hasExplicitFilters,
}) => {
  const [entries, setEntries]   = useState([]);
  const [loading, setLoading]   = useState(false);
  const hasAutoSelected         = useRef(false);
  const prevClientId            = useRef(null);

  // ── Firestore listener ────────────────────────────────────────
    useEffect(() => {
    if (!clientId) {
      setEntries([]);
      setLoading(false);
      return;
    }

    // Reset auto-select when client changes
    if (prevClientId.current !== clientId) {
      hasAutoSelected.current = false;
      prevClientId.current = clientId;
    }

    setLoading(true);

    const q = query(
      collection(db, 'clients', clientId, 'financialIntelligence'),
      orderBy('createdAt', 'desc')
    );

    const unsubscribe = onSnapshot(q,
            (snapshot) => {
        const docs = snapshot.docs
          .map(doc => ({ id: doc.id, ...doc.data() }))
          .filter(e => e.status !== 'generating'); // Hide in-progress entries from history

        setEntries(docs);
        setLoading(false);

        // Auto-select most recent entry on first load
        if (docs.length > 0 && !hasAutoSelected.current && !activeEntryId) {
          hasAutoSelected.current = true;
          onEntrySelect(docs[0]);
          return;
        }

        // Keep the currently selected entry fresh when Firestore updates it
        if (activeEntryId) {
          const refreshedActiveEntry = docs.find(e => e.id === activeEntryId);
          if (refreshedActiveEntry) {
            onEntrySelect(refreshedActiveEntry);
          }
        }

        // Notify parent when a streamed entry is now persisted
        if (streamingEntryId && streamingEntryId !== 'pending') {
          const persistedEntry = docs.find(e => e.id === streamingEntryId);
          if (persistedEntry) {
            onStreamingEntryPersisted(streamingEntryId);
            onEntrySelect(persistedEntry);
          }
        }

        // If streaming was 'pending' (no entryId yet) and a new entry just appeared, select it
        if (streamingEntryId === 'pending' && docs.length > 0) {
          const newest = docs[0];
          if (!activeEntryId || newest.id !== activeEntryId) {
            onStreamingEntryPersisted(newest.id);
            onEntrySelect(newest);
          }
        }
      },
      (error) => {
        console.error('[FIThreadPanel] Firestore listener error:', error);
        setLoading(false);
      }
    );

        return () => unsubscribe();
  }, [clientId, activeEntryId, streamingEntryId, onEntrySelect, onStreamingEntryPersisted]);

    const handleStarToggle = async (entry) => {
    try {
      const entryRef = doc(db, 'clients', clientId, 'financialIntelligence', entry.id);
      await updateDoc(entryRef, { starred: !entry.starred });
    } catch (err) {
      console.error('[FIThreadPanel] Star toggle failed:', err);
    }
  };

    const filteredEntries = useMemo(() => {
    const normalizedQuery = searchQuery.trim().toLowerCase();

    let next = [...entries];

    if (normalizedQuery) {
      next = next.filter((entry) => {
        const labelKey = entry.triggerLabel || entry.situationKey || entry.triggerType;
        const label = HISTORY_LABELS[labelKey] || labelKey || '';
        const summary = entry.financialIntelligenceSummary || '';
        const prompt = entry.rawPrompt || '';
        const output = entry.outputMarkdown || '';

        return `${label} ${summary} ${prompt} ${output}`.toLowerCase().includes(normalizedQuery);
      });
    }

    if (dateFrom || dateTo) {
      const fromTs = dateFrom ? new Date(dateFrom + 'T00:00:00') : null;
      const toTs   = dateTo   ? new Date(dateTo + 'T23:59:59') : null;

      next = next.filter((entry) => {
        const entryDate = entry.createdAt?.toDate?.()
          || (entry.createdAt ? new Date(entry.createdAt) : null);

        if (!entryDate) return false;
        if (fromTs && entryDate < fromTs) return false;
        if (toTs && entryDate > toTs) return false;
        return true;
      });
    }

    if (sortOption === 'oldest') {
      next.sort((a, b) => {
        const aTime = a.createdAt?.toMillis?.() || 0;
        const bTime = b.createdAt?.toMillis?.() || 0;
        return aTime - bTime;
      });
    } else if (sortOption === 'starred') {
      next.sort((a, b) => {
        if (!!b.starred !== !!a.starred) return Number(b.starred) - Number(a.starred);
        const aTime = a.createdAt?.toMillis?.() || 0;
        const bTime = b.createdAt?.toMillis?.() || 0;
        return bTime - aTime;
      });
    } else {
      next.sort((a, b) => {
        const aTime = a.createdAt?.toMillis?.() || 0;
        const bTime = b.createdAt?.toMillis?.() || 0;
        return bTime - aTime;
      });
    }

    return next;
  }, [entries, searchQuery, sortOption, dateFrom, dateTo]);

    useEffect(() => {
    if (onFilteredCountChange) onFilteredCountChange(filteredEntries.length);
  }, [filteredEntries.length, onFilteredCountChange]);

  // ── No client selected ────────────────────────────────────────
  if (!clientId) {
    return (
      <Box sx={{ p: 2, textAlign: 'center' }}>
        <Typography variant="caption" color="text.disabled">
          Select a client to view analyses
        </Typography>
      </Box>
    );
  }

  // ── Loading ───────────────────────────────────────────────────
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', pt: 4 }}>
        <CircularProgress size={20} />
      </Box>
    );
  }

  // ── Empty state ───────────────────────────────────────────────
  if (entries.length === 0 && !streamingEntryId) {
    return (
      <Box sx={{ p: 2.5, textAlign: 'center' }}>
        <FIIcon sx={{ fontSize: 36, color: 'text.disabled', mb: 1 }} />
        <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
          No financial analyses yet for this client.
        </Typography>
        <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mt: 0.5 }}>
          Start with a situation below or ask anything.
        </Typography>
      </Box>
    );
  }

    // hasExplicitFilters passed from parent — reflects user-set filters only,
  // not the implicit 90-day default window
  if (filteredEntries.length === 0 && entries.length > 0 && !streamingEntryId) {
    if (hasExplicitFilters) {
      return (
        <Box sx={{ p: 2.5, textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
            No analyses match your filters.
          </Typography>
          {onClearFilters && (
            <Button
              variant="text"
              size="small"
              onClick={onClearFilters}
              sx={{ mt: 0.5, fontSize: '0.78rem' }}
            >
              Clear filters
            </Button>
          )}
        </Box>
      );
    }

    if (!showAllHistory) {
      return (
        <Box sx={{ p: 2.5, textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
            No analyses in the last 90 days.
          </Typography>
          <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mt: 0.5, fontSize: '0.76rem' }}>
            Click "Show all history" in the filter panel to see older entries.
          </Typography>
          {onShowAllHistory && (
            <Button
              variant="text"
              size="small"
              onClick={onShowAllHistory}
              sx={{ mt: 0.5, fontSize: '0.78rem' }}
            >
              Show all history
            </Button>
          )}
        </Box>
      );
    }
  }

  // ── Streaming placeholder card ────────────────────────────────
  const showStreamingCard = !!streamingEntryId;

  return (
    <Box>
            {/* Header */}
      <Box sx={{ px: 2, py: 1.2, borderBottom: '1px solid', borderColor: 'divider' }}>
        <Typography variant="caption" fontWeight={600} color="text.secondary"
          sx={{ textTransform: 'uppercase', fontSize: '0.68rem', letterSpacing: '0.06em' }}>
          Analysis History
        </Typography>
      </Box>

      {/* Streaming placeholder */}
      {showStreamingCard && (
        <FIEntryCard
          entry={{ id: 'streaming', triggerLabel: 'freeform', financialIntelligenceSummary: '', dataSnapshot: null, starred: false, createdAt: null }}
          isActive={true}
          isStreaming={true}
          onClick={() => {}}
        />
      )}

            {/* Persisted entries */}
       {filteredEntries.map(entry => (
        <FIEntryCard
          key={entry.id}
          entry={entry}
          isActive={activeEntryId === entry.id}
          isStreaming={false}
          onClick={onEntrySelect}
          onStarToggle={handleStarToggle}
        />
      ))}
    </Box>
  );
};

export default FIThreadPanel;